/**
 * Kayıtlar state: the event feed (GET /log), loaded when the screen opens. New events are
 * never inserted on their own (design rule 10): a timer asks the server how many arrived
 * after the newest shown, the page shows "N yeni olay", and the tap reloads.
 */

import { useSyncExternalStore } from 'react';
import type { TwinApi } from '../api/client.js';
import type { LogView } from '../api/log.js';
import { useStore } from './context.js';

export const NEW_EVENTS_CHECK_MS = 60_000;

export interface LogState {
  view: LogView | null;
  error: string | null;
  loading: boolean;
  /** Events newer than the page, counted by the last check (capped at the check limit). */
  pending: number;
}

export class LogStore {
  private state: LogState = { view: null, error: null, loading: false, pending: 0 };
  private readonly listeners = new Set<() => void>();
  private inFlight: Promise<void> | null = null;

  constructor(private readonly api: TwinApi) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): LogState => this.state;

  private set(patch: Partial<LogState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  ensureLoaded(): Promise<void> {
    if (this.state.view !== null || this.inFlight !== null) return this.inFlight ?? Promise.resolve();
    return this.reload();
  }

  /** Reads the page again (also the "N yeni olay" tap). */
  reload(): Promise<void> {
    this.set({ loading: true });
    const run = (async () => {
      try {
        const view = await this.api.getLog({});
        this.set({ view, error: null, loading: false, pending: 0 });
      } catch (error) {
        this.set({ error: error instanceof Error ? error.message : String(error), loading: false });
      }
    })();
    this.inFlight = run;
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null;
    });
    return run;
  }

  /** Counts the events after the newest shown; never changes the page. */
  async checkNew(): Promise<void> {
    const newest = this.state.view?.newestAt;
    if (newest === undefined || newest === null || this.inFlight !== null) return;
    try {
      const fresh = await this.api.getLog({ since: newest, limit: 100 });
      this.set({ pending: fresh.events.length });
    } catch {
      // A failed check changes nothing; the next tick tries again.
    }
  }
}

export function useLog(): { store: LogStore; state: LogState } {
  const store = useStore().log;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { store, state };
}
